import React, { useMemo, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Phone, MapPin, Smartphone, BatteryCharging, Plug, Droplets, Camera, Layers, CheckCircle, Clock, Shield, Car } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Navigation from '../components/home/Navigation';
import Footer from '../components/home/Footer';
import VillesVoisines from '../components/VillesVoisines';
import AvisVille from '../components/AvisVille';
import { getVille, ZONES } from '../data/villes';
import { useSEO } from '../hooks/useSEO';

const SERVICES = [
  { icon: Smartphone, titre: 'Écran cassé', texte: 'Remplacement écran iPhone et Samsung (LCD, OLED, AMOLED).' },
  { icon: BatteryCharging, titre: 'Batterie', texte: 'Batterie haute capacité, autonomie retrouvée en 30 min.' },
  { icon: Plug, titre: 'Connecteur de charge', texte: 'Téléphone qui ne charge plus ou câble mal reconnu.' },
  { icon: Layers, titre: 'Vitre arrière', texte: 'Remplacement du dos en verre cassé.' },
  { icon: Droplets, titre: 'Dégâts des eaux', texte: 'Nettoyage et désoxydation après une chute dans l\'eau.' },
  { icon: Camera, titre: 'Caméra & micro', texte: 'Caméra floue, haut-parleur ou micro défaillant.' },
];

const domicileTexte = (km) => {
  if (km <= 15) return 'Intervention à domicile le jour même';
  if (km <= 40) return 'Intervention à domicile sur rendez-vous';
  return 'Déplacement à domicile possible sur demande';
};

const buildFaq = (ville) => [
  {
    q: `Réparez-vous les iPhone et Samsung à ${ville.nom} ?`,
    r: `Oui. Suro Phone répare tous les iPhone (SE, 8, X, 11, 12, 13, 14, 15, 16) et Samsung Galaxy (A, S, Note, Z) pour les habitants de ${ville.nom} (${ville.cp}) : écran, batterie, connecteur de charge, vitre arrière, caméra, dégâts des eaux.`,
  },
  {
    q: `Vous déplacez-vous à domicile à ${ville.nom} ?`,
    r: `Oui, nous intervenons à domicile à ${ville.nom} et dans les communes voisines. ${ville.nom} se situe à environ ${ville.km} km de notre atelier de Saint-Chamond. Vous pouvez aussi venir à l'atelier (40 rue de la République, Saint-Chamond), sur rendez-vous uniquement, du lundi au vendredi de 10h00 à 18h00 et le samedi de 10h00 à 19h00.`,
  },
  {
    q: `Combien coûte une réparation de téléphone à ${ville.nom} ?`,
    r: `Le tarif dépend du modèle et de la panne. Le devis est gratuit et immédiat : appelez le 07.73.60.15.21 ou envoyez un message WhatsApp avec votre modèle, nous répondons en quelques minutes. Toutes nos réparations sont garanties 3 mois.`,
  },
  {
    q: `Combien de temps dure la réparation ?`,
    r: `La plupart des réparations (écran, batterie, connecteur) sont réalisées en 30 minutes à 1 heure. Vous repartez le jour même avec votre téléphone réparé.`,
  },
];

const buildJsonLd = (ville, faq) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: `Réparation téléphone ${ville.nom}`,
      serviceType: 'Réparation smartphone iPhone et Samsung',
      provider: { '@id': 'https://surophone.fr' },
      areaServed: { '@type': 'City', name: ville.nom, postalCode: ville.cp },
      url: `https://surophone.fr/reparation-telephone/${ville.slug}`,
    },
    {
      '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.r } })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://surophone.fr/' },
        { '@type': 'ListItem', position: 2, name: "Zones d'intervention", item: 'https://surophone.fr/zones-intervention' },
        { '@type': 'ListItem', position: 3, name: ville.nom },
      ],
    },
  ],
});

const VillePage = () => {
  const { slug } = useParams();
  const ville = getVille(slug);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const faq = useMemo(() => (ville ? buildFaq(ville) : []), [ville]);
  const jsonLd = useMemo(() => (ville ? buildJsonLd(ville, faq) : null), [ville, faq]);

  useSEO({
    title: ville ? `Réparation téléphone ${ville.nom} (${ville.cp}) – iPhone & Samsung | Suro Phone` : 'Suro Phone',
    description: ville ? `Réparateur iPhone et Samsung à ${ville.nom} : écran, batterie, connecteur. À domicile ou à l'atelier de Saint-Chamond (${ville.km} km). Devis gratuit, garantie 3 mois. ☎ 07.73.60.15.21` : '',
    path: ville ? `/reparation-telephone/${ville.slug}` : '/',
    jsonLd,
  });

  if (!ville) return <Navigate to="/zones-intervention" replace />;
  if (ville.url) return <Navigate to={ville.url} replace />;

  const zone = ZONES[ville.zone];

  return (
    <div className="min-h-screen bg-white" data-testid="ville-page">
      <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />

      <section className="bg-gradient-to-br from-[#1a2332] to-[#1e2a3d] text-white py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <nav className="text-sm text-gray-400 mb-4" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-white">Accueil</Link> › <Link to="/zones-intervention" className="hover:text-white">Zones d'intervention</Link> › <span className="text-white">{ville.nom}</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400 rounded-full px-4 py-2 mb-5 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{ville.nom} ({ville.cp}) • à {ville.km} km de l'atelier</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-5 leading-tight" data-testid="ville-h1">
            Réparation téléphone à {ville.nom} – iPhone & Samsung
          </h1>
          <p className="text-lg md:text-xl text-gray-300 mb-8">
            Écran cassé, batterie HS, connecteur de charge ? Suro Phone répare votre smartphone le jour même pour les habitants de {ville.nom}. {domicileTexte(ville.km)}, ou à l'atelier de Saint-Chamond. Devis gratuit, garantie 3 mois.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="tel:0773601521" data-testid="ville-call-btn" className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl transition-colors">
              <Phone className="w-5 h-5" /> Appeler – devis gratuit
            </a>
            <a href={`https://wa.me/33773601521?text=${encodeURIComponent(`Bonjour, j'habite ${ville.nom} et je souhaite un devis pour réparer mon téléphone.`)}`} target="_blank" rel="noopener noreferrer" data-testid="ville-whatsapp-btn" className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl transition-colors">
              WhatsApp
            </a>
            <Link to="/rendez-vous" data-testid="ville-rdv-btn" className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl transition-colors">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Votre réparateur de smartphone près de {ville.nom}</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            {ville.nom} ({ville.cp}, environ {ville.hab} habitants) se trouve {zone.texte}. Depuis notre atelier de <Link to="/reparation-iphone-saint-chamond" className="text-blue-600 font-semibold hover:underline">Saint-Chamond</Link>, nous accompagnons chaque semaine des clients de {ville.nom} pour la <strong>réparation d'iPhone</strong> et de <strong>Samsung Galaxy</strong> : changement d'écran, remplacement de batterie, connecteur de charge, vitre arrière, caméra ou dégâts des eaux.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Plutôt que de vous déplacer jusqu'à Saint-Étienne ou Lyon, profitez d'un <strong>réparateur téléphone local</strong>, joignable directement par téléphone ou WhatsApp, qui utilise des pièces Premium testées et garantit chaque intervention 3 mois.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="flex items-center gap-3 bg-green-50 rounded-xl p-4"><Clock className="w-6 h-6 text-green-600" /><span className="font-semibold text-gray-900">Réparé le jour même</span></div>
            <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4"><Shield className="w-6 h-6 text-blue-600" /><span className="font-semibold text-gray-900">Garantie 3 mois</span></div>
            <div className="flex items-center gap-3 bg-yellow-50 rounded-xl p-4"><Car className="w-6 h-6 text-yellow-600" /><span className="font-semibold text-gray-900">À domicile à {ville.nom}</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Réparations proposées à {ville.nom}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map(({ icon: Icon, titre, texte }) => (
              <div key={titre} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <Icon className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-1">{titre}</h3>
                <p className="text-gray-600 text-sm mb-3">{texte}</p>
                <span className="text-blue-600 font-bold text-sm">Devis gratuit</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Comment ça se passe pour les habitants de {ville.nom} ?</h2>
          <ol className="space-y-4">
            {[
              ['Appelez ou envoyez un WhatsApp', 'Indiquez votre modèle et votre panne : devis gratuit en quelques minutes.'],
              ['Choisissez : domicile ou atelier', `Nous venons chez vous à ${ville.nom} ou vous passez à l'atelier de Saint-Chamond, sur rendez-vous.`],
              ['Repartez avec un téléphone réparé', 'Intervention en 30 min à 1 h pour la plupart des pannes, garantie 3 mois incluse.'],
            ].map(([t, d], i) => (
              <li key={t} className="flex gap-4 items-start">
                <span className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <div><p className="font-bold text-gray-900">{t}</p><p className="text-gray-600">{d}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Questions fréquentes – {ville.nom}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faq.map((f) => (
              <AccordionItem key={f.q} value={f.q} className="border rounded-lg px-5 bg-white">
                <AccordionTrigger className="text-left font-semibold text-gray-900">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600">{f.r}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <AvisVille slug={ville.slug} nom={ville.nom} />

      <VillesVoisines slug={ville.slug} titre={`Nous intervenons aussi autour de ${ville.nom}`} />

      <section className="py-14 px-4 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Besoin d'une réparation à {ville.nom} ?</h2>
          <p className="text-blue-100 mb-6 flex items-center justify-center gap-2"><CheckCircle className="w-5 h-5" /> Devis gratuit • Le jour même • Garantie 3 mois</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:0773601521" className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg">📞 07.73.60.15.21</a>
            <Link to="/rendez-vous" className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg">Prendre rendez-vous</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VillePage;
